import type { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { isStoreOnlyUser } from "@/lib/portal-store";

/**
 * Route guard for portal pages. Mirrors apps/web's RequireAuth but pushes
 * unauth visitors to /login (portal's signed-out home).
 *
 * Company stores (2026-10-12): a store employee (role `store_member`, no
 * `client`) only has the store. Every page that isn't marked `store` sends
 * them to /store. The API refuses them on those routes anyway
 * (requireClient); this keeps them off pages that would only error.
 */
export function RequireAuth({ children, store = false }: { children: ReactNode; store?: boolean }) {
  const { user, loading, unreachable, unreachableGaveUp, unreachableMessage, retryNow } = useAuth();
  const location = useLocation();

  if (unreachable && unreachableGaveUp && !user) {
    // Gave up (several rounds, or a definitive server answer): say what the
    // server said and offer a way out.
    return (
      <div role="alert" className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-lg font-medium">Can&apos;t restore your session right now</p>
        <p className="max-w-md text-sm opacity-70">{unreachableMessage ?? "The server isn't answering. Try again in a few minutes."}</p>
        <Link to="/login" className="rounded-md border px-4 py-2 text-sm font-medium">
          Sign in again
        </Link>
      </div>
    );
  }

  if (loading && unreachable) {
    // A deploy or outage on page load (stay signed in, 2026-10-22): say so
    // and keep retrying — never bounce a signed-in person to the sign-in page.
    return (
      <div role="alert" className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-lg font-medium">Can&apos;t reach the client portal — retrying…</p>
        <p className="text-sm opacity-70">You&apos;re still signed in. This usually takes under a minute.</p>
        <button type="button" onClick={retryNow} className="rounded-md border px-4 py-2 text-sm font-medium">
          Retry now
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-cream-50">
        <div
          className="h-10 w-10 animate-pulse rounded-md bg-peach-400"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (!store && isStoreOnlyUser(user)) {
    return <Navigate to="/store" replace />;
  }

  return <>{children}</>;
}
